import React from "react";
import { Terminal, Shield, Phone, AlertOctagon, RotateCcw } from "lucide-react";
import type { SowFields } from "../../../../../utils/api/endpoints/sow";

interface AppendixARulesOfEngagementEditorProps {
  fields: SowFields;
  setField: <K extends keyof SowFields>(key: K, value: SowFields[K]) => void;
}

export const AppendixARulesOfEngagementEditor: React.FC<AppendixARulesOfEngagementEditorProps> = ({
  fields,
  setField
}) => {
  const defaultRoEIntro =
    "This Appendix forms part of this Statement of Work and sets out the operational rules under which testing is authorised and performed. Fields marked \"To be confirmed\" will be completed and confirmed in writing by both parties before testing commences; completion of this Appendix is a condition precedent to the commencement of testing. Where this Appendix conflicts with the body of this Statement of Work, the body prevails.";

  const defaultProhibited = `Intentionally destroy, encrypt, bulk-download, or otherwise remove Client production data from the Client environment, except for the minimum evidence reasonably necessary to demonstrate and document a confirmed finding, which will be recorded within the finding.
Modify or delete Client user accounts, records, or configuration, other than accounts and records created by CTB for testing purposes.
Install persistence mechanisms, backdoors, or any change intended to survive the testing window. Any test artefact created by CTB will be recorded and removed at the conclusion of testing.
Use credentials, tokens, or access obtained during testing beyond what is required to demonstrate the finding.
Test any host, service, application, or address not listed as an authorised target.`;

  const defaultStopTesting = `Requesting a pause: Either party may request an immediate pause at any time by telephoning the other party's emergency contact, followed by written confirmation by email.
CTB response: CTB will suspend all testing activity immediately on receipt of a pause request and confirm suspension in writing.
CTB-initiated pause: CTB will pause testing on its own initiative, and notify the Client without delay, where it identifies a material unintended effect on the production environment, a critical vulnerability warranting immediate attention, or evidence of a pre-existing compromise.
Resumption: Testing resumes only once both parties have agreed in writing, and only against the targets confirmed as ready.
Effect on scope and timeline: Time lost to a pause does not reduce the agreed scope. The timeline will be adjusted in accordance with the Timeline Dependencies term.`;

  return (
    <div className="space-y-6">
      <div className="border-b border-slate-200 pb-4">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Terminal size={16} className="text-ctb-blue" />
          Appendix A: Rules of Engagement (RoE)
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Operational rules of engagement governing authorized testing execution, egress source IPs, testing accounts, emergency contacts, and stop-testing protocols.
        </p>
      </div>

      {/* RoE Intro */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800">Appendix A Introduction</label>
          <button
            type="button"
            onClick={() => setField("appendixRoEIntro", defaultRoEIntro)}
            className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
          >
            <RotateCcw size={11} />
            Reset to standard
          </button>
        </div>
        <textarea
          rows={3}
          value={fields.appendixRoEIntro ?? ""}
          onChange={(e) => setField("appendixRoEIntro", e.target.value)}
          className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
        />
      </div>

      {/* Operational Parameters Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-200">
        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Testing Environment</label>
          <input
            type="text"
            value={fields.testingEnvironment ?? ""}
            onChange={(e) => setField("testingEnvironment", e.target.value)}
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Services on Target Infrastructure</label>
          <input
            type="text"
            value={fields.servicesOnInfrastructure ?? ""}
            onChange={(e) => setField("servicesOnInfrastructure", e.target.value)}
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Public Availability Date</label>
          <input
            type="text"
            value={fields.publicAvailabilityDate ?? ""}
            onChange={(e) => setField("publicAvailabilityDate", e.target.value)}
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">CTB Testing Source IP (Allowlisting)</label>
          <input
            type="text"
            value={fields.testingSourceIp ?? ""}
            onChange={(e) => setField("testingSourceIp", e.target.value)}
            className="w-full text-xs font-mono bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">WAF / Rate Limiting Controls</label>
          <input
            type="text"
            value={fields.wafRateLimitingNote ?? ""}
            onChange={(e) => setField("wafRateLimitingNote", e.target.value)}
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Testing Window</label>
          <input
            type="text"
            value={fields.testingWindow ?? ""}
            onChange={(e) => setField("testingWindow", e.target.value)}
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Blackout Periods</label>
          <input
            type="text"
            value={fields.blackoutPeriods ?? ""}
            onChange={(e) => setField("blackoutPeriods", e.target.value)}
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Project Completion Date Definition</label>
          <input
            type="text"
            value={fields.projectCompletionDateNote ?? ""}
            onChange={(e) => setField("projectCompletionDateNote", e.target.value)}
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>

        <div className="space-y-1 md:col-span-2">
          <label className="text-[11px] font-semibold text-slate-600">Third-Party Authorisations Clause</label>
          <input
            type="text"
            value={fields.thirdPartyAuthorisationsText ?? ""}
            onChange={(e) => setField("thirdPartyAuthorisationsText", e.target.value)}
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>
      </div>

      {/* Payment & Identity Testing in RoE */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-200">
        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Payment Testing RoE Rule</label>
          <textarea
            rows={2}
            value={fields.paymentTestingNote ?? ""}
            onChange={(e) => setField("paymentTestingNote", e.target.value)}
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Identity Verification RoE Rule</label>
          <textarea
            rows={2}
            value={fields.identityVerificationNote ?? ""}
            onChange={(e) => setField("identityVerificationNote", e.target.value)}
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>
      </div>

      {/* Account Credentials */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-200">
        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Standard Access Account</label>
          <input
            type="text"
            value={fields.standardAccessAccount ?? ""}
            onChange={(e) => setField("standardAccessAccount", e.target.value)}
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Privileged / Admin Account</label>
          <input
            type="text"
            value={fields.privilegedAccessAccount ?? ""}
            onChange={(e) => setField("privilegedAccessAccount", e.target.value)}
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>

        <div className="md:col-span-2 space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Test Accounts Requirement Note</label>
          <input
            type="text"
            value={fields.testAccountsNote ?? ""}
            onChange={(e) => setField("testAccountsNote", e.target.value)}
            placeholder="e.g. 2 user accounts and 1 administrative account per target"
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>
      </div>

      {/* Emergency Contacts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-200">
        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600 flex items-center gap-1">
            <Phone size={11} className="text-slate-500" />
            Client Emergency Contact
          </label>
          <input
            type="text"
            value={fields.clientEmergencyContact ?? ""}
            onChange={(e) => setField("clientEmergencyContact", e.target.value)}
            placeholder="Name, role, phone, email"
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600 flex items-center gap-1">
            <Shield size={11} className="text-ctb-blue" />
            CTB Engagement Lead
          </label>
          <input
            type="text"
            value={fields.ctbEngagementLead ?? ""}
            onChange={(e) => setField("ctbEngagementLead", e.target.value)}
            placeholder="Lead Consultant Name & Phone"
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
          />
        </div>
      </div>

      {/* Prohibited Activities */}
      <div className="space-y-2 pt-2 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
            <AlertOctagon size={13} className="text-rose-600" />
            Prohibited Testing Activities
          </label>
          <button
            type="button"
            onClick={() => setField("prohibitedActivitiesText", defaultProhibited)}
            className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
          >
            <RotateCcw size={11} />
            Reset to standard
          </button>
        </div>
        <textarea
          rows={5}
          value={fields.prohibitedActivitiesText ?? ""}
          onChange={(e) => setField("prohibitedActivitiesText", e.target.value)}
          className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
        />
      </div>

      {/* Stop-Testing Procedure */}
      <div className="space-y-2 pt-2 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
            <Shield size={13} className="text-amber-600" />
            Stop-Testing and Emergency Procedure
          </label>
          <button
            type="button"
            onClick={() => setField("stopTestingProcedureText", defaultStopTesting)}
            className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
          >
            <RotateCcw size={11} />
            Reset to standard
          </button>
        </div>
        <textarea
          rows={5}
          value={fields.stopTestingProcedureText ?? ""}
          onChange={(e) => setField("stopTestingProcedureText", e.target.value)}
          className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
        />
      </div>
    </div>
  );
};
